// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';

import { buildExtensions } from '../RichTextEditor/extensions';
import { sanitizeHtml } from '../RichTextEditor/sanitizeHtml';
import Toolbar from '../RichTextEditor/Toolbar';
import Btn from '../claims/shared/Btn';
import '../RichTextEditor/RichTextEditor.css';
import { patchLetterSection } from '../../api/letterSectionsApi';
import {
  composeSectionsHtml,
  splitDocToSections,
  spliceSectionInDoc,
} from './letterDocMapping';

const SAVE_DEBOUNCE_MS = 1200;

const SAVE_ERROR_COPY =
  "Couldn't save this section. Your edits are still here — check your connection and it will retry on your next change.";
const LOCKED_COPY = 'This letter is approved and locked. Start a new version to make changes.';

const INDICATOR_TEXT = {
  idle: '',
  dirty: 'Unsaved changes',
  saving: 'Saving…',
  saved: 'Saved',
};

/** Which section owns `pos` (the last boundary at or before it). */
function sectionIdAtPos(doc, pos) {
  let currentId = null;
  doc.forEach((node, offset) => {
    if (node.type.name === 'sectionBoundary' && offset < pos) {
      currentId = node.attrs.sectionId;
    }
  });
  return currentId;
}

/**
 * ONE continuous rich-text editor over ALL letter sections (LTR-08). Replaces
 * the per-section card stack. Per-section LetterSection rows stay the source of
 * truth: manual edits are spliced back into the corresponding rows via the
 * existing per-section PATCH (patchLetterSection); a single-section regenerate +
 * refetch splices only that region back in.
 *
 * CLAUDE.md editor-safety invariants:
 *  - local editor state is the source of truth WHILE DIRTY, tracked PER SECTION
 *    (dirtyRef). A refetch splices a section in ONLY when its updated_at advanced
 *    AND it is not dirty AND the caret isn't in that region — a dirty region is
 *    never overwritten and focus is retained.
 *  - the editor is created ONCE; onAlert/onSaveStateChange/onRequestNewVersion
 *    live in refs so callback identity never re-keys it.
 *  - debounce timers are cleared on unmount.
 *
 * @param {{
 *   sections: Array<object>,
 *   letterType?: string,
 *   locked?: boolean,
 *   resolveVariable?: (key: string) => string,
 *   onSaveStateChange?: (sectionId: string, state: string) => void,
 *   onRequestNewVersion?: () => void,
 *   onAlert?: (message: string) => void,
 *   editorRef?: { current: any },
 * }} props
 */
export default function UnifiedLetterEditor({
  sections = [],
  locked = false,
  resolveVariable = null,
  onSaveStateChange,
  onRequestNewVersion,
  onAlert,
  editorRef: externalEditorRef,
}) {
  const [saveStates, setSaveStates] = useState({}); // sectionId -> idle|dirty|saving|saved
  const [saveError, setSaveError] = useState(null); // null|'locked'|'network'

  const baselineRef = useRef(new Map()); // sectionId -> { updated_at, html }
  const baselineInitRef = useRef(false);
  const dirtyRef = useRef(new Set());
  const timersRef = useRef(new Map()); // sectionId -> timeout id
  const focusedSectionRef = useRef(null);
  const applyingRef = useRef(false); // suppress dirty tracking during splice/recompose
  const sectionsRef = useRef(sections);
  sectionsRef.current = sections;

  const localEditorRef = useRef(null);
  const editorRef = externalEditorRef || localEditorRef;

  const onSaveStateChangeRef = useRef(onSaveStateChange);
  const onRequestNewVersionRef = useRef(onRequestNewVersion);
  const onAlertRef = useRef(onAlert);
  onSaveStateChangeRef.current = onSaveStateChange;
  onRequestNewVersionRef.current = onRequestNewVersion;
  onAlertRef.current = onAlert;

  const reportState = useCallback((sectionId, state) => {
    setSaveStates((prev) => (prev[sectionId] === state ? prev : { ...prev, [sectionId]: state }));
    onSaveStateChangeRef.current?.(sectionId, state);
  }, []);

  const initialContent = useMemo(() => composeSectionsHtml(sections), []); // eslint-disable-line react-hooks/exhaustive-deps

  const doSave = useCallback(
    async (sectionId) => {
      const editor = editorRef.current;
      if (!editor) return;
      const entry = splitDocToSections(editor).find((s) => s.sectionId === sectionId);
      if (!entry) return;
      // Skip a body that serialized to empty — never destructively blank a row.
      if (!entry.html) {
        dirtyRef.current.delete(sectionId);
        reportState(sectionId, 'idle');
        return;
      }
      const base = baselineRef.current.get(sectionId);
      if (base && entry.html === base.html) {
        dirtyRef.current.delete(sectionId);
        reportState(sectionId, 'saved');
        return;
      }
      reportState(sectionId, 'saving');
      setSaveError(null);
      try {
        const data = await patchLetterSection(sectionId, {
          content_json: entry.json,
          content_html: entry.html,
        });
        baselineRef.current.set(sectionId, {
          updated_at: data?.updated_at ?? base?.updated_at,
          html: entry.html,
        });
        dirtyRef.current.delete(sectionId);
        reportState(sectionId, 'saved');
      } catch (err) {
        if (err?.response?.status === 409) {
          setSaveError('locked');
        } else {
          setSaveError('network');
          onAlertRef.current?.(SAVE_ERROR_COPY);
        }
        // Edits are retained (dirty ownership); retries on the next change.
        reportState(sectionId, 'dirty');
      }
    },
    [editorRef, reportState]
  );

  const scheduleSave = useCallback(
    (sectionId) => {
      const timers = timersRef.current;
      if (timers.has(sectionId)) clearTimeout(timers.get(sectionId));
      timers.set(
        sectionId,
        setTimeout(() => {
          timers.delete(sectionId);
          doSave(sectionId);
        }, SAVE_DEBOUNCE_MS)
      );
    },
    [doSave]
  );

  const handleUpdate = useCallback(
    (editor) => {
      if (applyingRef.current) return; // splice/recompose is not a user edit
      const split = splitDocToSections(editor);
      split.forEach((entry) => {
        const base = baselineRef.current.get(entry.sectionId);
        const baseHtml = base?.html ?? '';
        if (entry.html === baseHtml) {
          if (dirtyRef.current.has(entry.sectionId)) {
            dirtyRef.current.delete(entry.sectionId);
            const timers = timersRef.current;
            if (timers.has(entry.sectionId)) {
              clearTimeout(timers.get(entry.sectionId));
              timers.delete(entry.sectionId);
            }
            reportState(entry.sectionId, 'saved');
          }
          return;
        }
        dirtyRef.current.add(entry.sectionId);
        setSaveError(null);
        reportState(entry.sectionId, 'dirty');
        if (entry.html) {
          scheduleSave(entry.sectionId);
        }
      });
    },
    [reportState, scheduleSave]
  );

  const editor = useEditor(
    {
      extensions: buildExtensions({ resolveVariable, sectionBoundaries: true }),
      content: initialContent,
      editable: !locked,
      editorProps: {
        attributes: { 'aria-label': 'Letter content' },
        transformPastedHTML: (html) => sanitizeHtml(html),
      },
      onCreate: ({ editor: ed }) => {
        editorRef.current = ed;
      },
      onUpdate: ({ editor: ed }) => handleUpdate(ed),
      onSelectionUpdate: ({ editor: ed }) => {
        if (applyingRef.current) return;
        focusedSectionRef.current = ed.isFocused
          ? sectionIdAtPos(ed.state.doc, ed.state.selection.from)
          : focusedSectionRef.current;
      },
      onFocus: ({ editor: ed }) => {
        focusedSectionRef.current = sectionIdAtPos(ed.state.doc, ed.state.selection.from);
      },
      onBlur: () => {
        focusedSectionRef.current = null;
      },
    },
    [] // created ONCE — content/dirty ownership handled via refs + splice effect
  );

  useEffect(() => {
    if (editorRef) editorRef.current = editor;
  }, [editor, editorRef]);

  // Baseline init in a deterministic effect (NOT onCreate): onCreate can fire
  // deferred under fake timers and re-read a doc that already has local edits,
  // which would poison the baseline. The effect runs during render()'s flush,
  // before any user interaction. Baseline is editor-derived (matches what
  // onUpdate serializes — incl. resolved variable chips — so no false dirty).
  useEffect(() => {
    if (!editor || baselineInitRef.current) return;
    const byId = new Map(sectionsRef.current.map((s) => [s.id, s]));
    const next = new Map();
    splitDocToSections(editor).forEach((entry) => {
      next.set(entry.sectionId, {
        updated_at: byId.get(entry.sectionId)?.updated_at ?? null,
        html: entry.html,
      });
    });
    baselineRef.current = next;
    baselineInitRef.current = true;
  }, [editor]);

  useEffect(() => {
    // setEditable emits a spurious onUpdate by default (TipTap >= 2.5) — suppress it.
    if (editor) editor.setEditable(!locked, false);
  }, [editor, locked]);

  // Server-content sync: splice advanced, non-dirty, non-focused sections back in.
  const sig = sections.map((s) => `${s.id}:${s.updated_at}`).join('|');
  useEffect(() => {
    if (!editor || !baselineInitRef.current) return;
    const incoming = sectionsRef.current;
    const incomingIds = incoming.map((s) => s.id).join('|');
    const baselineIds = Array.from(baselineRef.current.keys()).join('|');

    if (incomingIds !== baselineIds) {
      // New section set (e.g. a new version). Recompose the whole doc — but only
      // when NOT dirty; a dirty local edit is kept (per-section rule).
      if (dirtyRef.current.size === 0) {
        applyingRef.current = true;
        editor.commands.setContent(composeSectionsHtml(incoming), false);
        applyingRef.current = false;
        baselineRef.current = new Map();
        const byId = new Map(incoming.map((s) => [s.id, s]));
        splitDocToSections(editor).forEach((entry) => {
          baselineRef.current.set(entry.sectionId, {
            updated_at: byId.get(entry.sectionId)?.updated_at ?? null,
            html: entry.html,
          });
        });
      }
      return;
    }

    incoming.forEach((section) => {
      const base = baselineRef.current.get(section.id);
      if (!base) return;
      if (!section.updated_at || section.updated_at === base.updated_at) return;
      const advanced =
        !base.updated_at ||
        new Date(section.updated_at).getTime() > new Date(base.updated_at).getTime();
      if (!advanced) return;
      if (dirtyRef.current.has(section.id)) return; // never clobber a dirty region
      if (focusedSectionRef.current === section.id) return; // caret is here
      applyingRef.current = true;
      spliceSectionInDoc(editor, section.id, section.content_html || '');
      applyingRef.current = false;
      const fresh = splitDocToSections(editor).find((s) => s.sectionId === section.id);
      baselineRef.current.set(section.id, {
        updated_at: section.updated_at,
        html: fresh?.html ?? '',
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, sig]);

  useEffect(
    () => () => {
      timersRef.current.forEach((id) => clearTimeout(id));
      timersRef.current.clear();
    },
    []
  );

  const handleLockedClick = useCallback(() => {
    if (locked) onRequestNewVersionRef.current?.();
  }, [locked]);

  const states = Object.values(saveStates);
  const aggregate = states.includes('saving')
    ? 'saving'
    : states.includes('dirty')
      ? 'dirty'
      : states.includes('saved')
        ? 'saved'
        : 'idle';
  const indicatorText = INDICATOR_TEXT[aggregate] || '';

  if (!editor) return null;

  return (
    <div className={`ab rte${locked ? ' rte--view' : ''}`}>
      {!locked && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            marginBottom: 8,
          }}
        >
          <div style={{ flex: 1 }}>
            <Toolbar editor={editor} />
          </div>
          {indicatorText && (
            <span
              style={{
                fontSize: 'var(--ab-text-xs)',
                color: aggregate === 'dirty' ? 'var(--ab-pending)' : 'var(--ab-fg-subtle)',
              }}
            >
              {indicatorText}
            </span>
          )}
        </div>
      )}

      {/* Locked letters gate edits through the Start-a-new-version modal —
          never a silent no-op. */}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
      <div
        className="rte-body"
        onClick={locked ? handleLockedClick : undefined}
        style={{
          background: locked ? 'var(--ab-bg-alt)' : undefined,
          cursor: locked ? 'pointer' : undefined,
        }}
      >
        <EditorContent editor={editor} />
      </div>

      {locked && (
        <div
          style={{
            marginTop: 8,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 'var(--ab-text-xs)',
            color: 'var(--ab-fg-subtle)',
          }}
        >
          Locked — approved version
        </div>
      )}

      {saveError === 'locked' && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            marginTop: 16,
            fontSize: 'var(--ab-text-sm)',
            color: 'var(--ab-fg)',
          }}
        >
          <span>{LOCKED_COPY}</span>
          <Btn kind="secondary" size="sm" onClick={() => onRequestNewVersionRef.current?.()}>
            Start new version
          </Btn>
        </div>
      )}
      {saveError === 'network' && (
        <div
          style={{
            marginTop: 16,
            fontSize: 'var(--ab-text-sm)',
            color: 'var(--ab-denied)',
          }}
        >
          {SAVE_ERROR_COPY}
        </div>
      )}
    </div>
  );
}
